<script lang="ts" setup>
import { ref } from '#imports'
import LoginDialog from '@/layouts/common/LoginDialog.vue'
import HeaderBarAvatar from '~/layouts/common/HeaderBarAvatar.vue'
import { useUserInfo } from '~/store/userInfo'
import { RoutePath } from '~/types/enum'
import I18nMenu from './I18nMenu.vue'
import LogoLightSVG from './LogoLightSVG.vue'
import LogoSVG from './LogoSVG.vue'

interface Props {
  theme?: 'default' | 'light'
  size?: 'default' | 'sm'
  onClick?: (type: 'lang' | 'login', ...args: unknown[]) => void
}

const props = withDefaults(defineProps<Props>(), { theme: 'default', size: 'default' })

const userInfo = useUserInfo()

const loginDialogRef = ref<InstanceType<typeof LoginDialog>>()

const showLogin = (callback?: () => void) => loginDialogRef.value?.show({ afterLogin: callback })

const headerLogin = () => {
  props.onClick?.('login')
  showLogin()
}

const handleLogin = async () => {
  if (!(await window.loginUI.isLoginStateValid())) {
    headerLogin()
  }
}

defineExpose({ showLogin })
</script>

<template>
  <header id="simple_header">
    <div
      :data-theme="theme"
      class="header-content"
      :class="size"
    >
      <div>
        <NuxtLinkLocale
          :to="RoutePath.HOME"
          style="display: flex"
        >
          <LogoLightSVG v-if="props.theme === 'light'" />
          <LogoSVG v-else />
        </NuxtLinkLocale>
      </div>
      <div class="info">
        <I18nMenu
          :theme="props.theme"
          @switch-lang="code => onClick?.('lang', code)"
        />
        <div
          v-if="userInfo.userId"
          class="avatar-container"
          @click="handleLogin"
        >
          <HeaderBarAvatar :show-user-nav="true" />
        </div>
        <ElButton
          v-else
          :data-theme="theme"
          class="pp-link-button sign-up-in"
          @click="headerLogin"
        >
          {{ $t('basic-sign-up-or-in') }}
        </ElButton>
      </div>
    </div>
    <LoginDialog ref="loginDialogRef" />
  </header>
</template>

<style lang="less" scoped>
@import 'assets/styles/util.less';

header {
  width: 100%;
  height: 60px;
  background: transparent;

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    max-width: calc(var(--pc-max-width) + 40px);
    padding: 0 20px;
    margin: auto;
    &.sm {
      @media screen and (max-width: 1680px) {
        max-width: 1000px;
      }
    }

    > div {
      display: flex;
      align-items: center;
    }
  }

  .info {
    .avatar-container {
      transition: margin-left var(--transition-delay) var(--transition-animation);
      margin-left: 48px;
      display: flex;
      align-items: center;

      a {
        text-decoration: none;
        border: 1px solid #306eff;
        border-radius: 4px;
        margin-left: 24px;
        color: #306eff;
        font-weight: 600;
        font-size: 14px;
        line-height: 22px;
        padding: 7px 5.5px;
      }
    }
  }

  .sign-up-in {
    margin-left: 30px;
    margin-right: 10px;
    padding: 16px;
    border: 1px solid var(--color-primary);
    border-radius: 4px;
    font-weight: 600;
    color: var(--color-primary) !important;
    background: transparent;

    &[data-theme='light'] {
      border: 1px solid #fff;
      color: #fff !important;
    }
  }

  &.over-sliding-height {
    border-bottom: solid 1px rgba(0, 0, 0, 0.08);
    background: #fff;
    position: fixed;
    margin-bottom: 0;
  }
}

@media screen and (max-width: 1200px) {
  header {
    .nav {
      margin-left: 24px;
    }

    .info .avatar-container {
      margin-left: 24px;
    }
  }
}
</style>
<style lang="less">
@media screen and (max-width: 768px) {
  .pp-header-bar-avatar-popover .user-info {
    --width: 290px;
    margin: 0;
  }
}

.el-button--primary .logout-confirm-btn {
}

.header-content {
  &[data-theme='light'] {
    .avatar-wrapper {
      .not-premium,
      .is-premium {
        border: 0;
      }
    }
  }
}
</style>
